import type { PlayerAvatar, SaveData, VaultId } from "../domain/models";
import { rankFor } from "../domain/systems";
import { LocalStorageDriver, type StorageDriver } from "../core/storage/LocalStorageDriver";

const KEY = "diamond-rush-save";
const validVaults = new Set<VaultId>(["street", "bank", "casino", "museum", "server", "ghost"]);
const validAvatars = new Set<PlayerAvatar>(["cracker", "hacker", "ghost", "king", "breaker"]);

export interface RunStatisticsUpdate {
  bestCombo: number;
  survivalSeconds: number;
  highestHeat: number;
  masterVaultWin: boolean;
  rareGems: number;
  hazardsAvoided: number;
  hazardsDestroyed: number;
  specialDiamonds: number;
  endlessScore: number;
}

export const defaultSave = (): SaveData => ({
  version: 1,
  highScore: 0,
  totalRuns: 0,
  totalDiamondsCollected: 0,
  totalGold: 0,
  rewardedAdsWatched: 0,
  onboardingCompleted: false,
  onboardingSkipped: false,
  onboardingRewardClaimed: false,
  discoveredFeatures: [],
  lastSeenWhatsNewVersion: null,
  tutorialStartedCount: 0,
  tutorialCompleted: false,
  tutorialSkipped: false,
  discoveryCardsSeenCount: 0,
  playerXp: 0,
  playerRank: 1,
  unlockedVaults: ["street"],
  achievements: [],
  campaignStars: {},
  highestUnlockedLevel: 1,
  endlessUnlocked: false,
  relics: [],
  profile: {
    name: "",
    avatar: "cracker",
  },
  statistics: {
    bestCombo: 0,
    longestSurvivalSeconds: 0,
    highestHeat: 0,
    masterVaultWins: 0,
    rareGemsCollected: 0,
    hazardsAvoided: 0,
    hazardsDestroyed: 0,
    specialDiamondsCollected: 0,
    endlessBestScore: 0,
  },
  settings: {
    reducedMotion: false,
    soundEnabled: true,
    musicEnabled: true,
  },
});

export class SaveRepository {
  private readonly driver: StorageDriver;

  constructor(storage: Pick<Storage, "getItem" | "setItem"> | StorageDriver) {
    this.driver = "read" in storage ? storage : new LocalStorageDriver(storage);
  }

  load(): SaveData {
    try {
      const raw = this.driver.read(KEY);
      if (!raw) return defaultSave();
      const parsed = JSON.parse(raw) as Partial<SaveData>;
      if (typeof parsed.version === "number" && parsed.version > 1) return defaultSave();
      return this.migrate(parsed);
    } catch {
      return defaultSave();
    }
  }

  save(data: SaveData): void {
    this.driver.write(KEY, JSON.stringify(data));
  }

  completeRun(
    data: SaveData,
    score: number,
    diamonds: number,
    gold: number,
    xp: number,
    stats?: RunStatisticsUpdate,
  ): SaveData {
    const nextXp = data.playerXp + xp;
    const totalRuns = data.totalRuns + 1;
    const playerRank = rankFor(nextXp);
    const unlocked = new Set<VaultId>(data.unlockedVaults);
    if (totalRuns >= 15 || playerRank >= 3) unlocked.add("bank");
    const next: SaveData = {
      ...data,
      highScore: Math.max(data.highScore, score),
      totalRuns,
      totalDiamondsCollected: data.totalDiamondsCollected + diamonds,
      totalGold: data.totalGold + gold,
      playerXp: nextXp,
      playerRank,
      unlockedVaults: [...unlocked],
      statistics: stats ? {
        bestCombo: Math.max(data.statistics.bestCombo, stats.bestCombo),
        longestSurvivalSeconds: Math.max(
          data.statistics.longestSurvivalSeconds,
          stats.survivalSeconds,
        ),
        highestHeat: Math.max(data.statistics.highestHeat, stats.highestHeat),
        masterVaultWins: data.statistics.masterVaultWins + (stats.masterVaultWin ? 1 : 0),
        rareGemsCollected: data.statistics.rareGemsCollected + stats.rareGems,
        hazardsAvoided: data.statistics.hazardsAvoided + stats.hazardsAvoided,
        hazardsDestroyed: data.statistics.hazardsDestroyed + stats.hazardsDestroyed,
        specialDiamondsCollected:
          data.statistics.specialDiamondsCollected + stats.specialDiamonds,
        endlessBestScore: Math.max(data.statistics.endlessBestScore, stats.endlessScore),
      } : data.statistics,
    };
    this.save(next);
    return next;
  }

  completeCampaignLevel(data: SaveData, level: number, stars: number): SaveData {
    const campaignStars = {
      ...data.campaignStars,
      [String(level)]: Math.max(data.campaignStars[String(level)] ?? 0, stars),
    };
    const highestUnlockedLevel = Math.min(30, Math.max(data.highestUnlockedLevel, level + 1));
    const unlocked = new Set<VaultId>(data.unlockedVaults);
    const chapterVaults: VaultId[] = ["street", "bank", "casino", "museum", "server", "ghost"];
    unlocked.add(chapterVaults[Math.floor((highestUnlockedLevel - 1) / 5)]);
    const next = {
      ...data,
      campaignStars,
      highestUnlockedLevel,
      endlessUnlocked: data.endlessUnlocked || level >= 10,
      unlockedVaults: [...unlocked],
    };
    this.save(next);
    return next;
  }

  unlockRelic(data: SaveData, relicId: string): SaveData {
    if (data.relics.includes(relicId)) return data;
    const next = { ...data, relics: [...data.relics, relicId] };
    this.save(next);
    return next;
  }

  recordRewardedAd(data: SaveData, bonusGold = 0): SaveData {
    const next: SaveData = {
      ...data,
      totalGold: data.totalGold + Math.max(0, Math.floor(bonusGold)),
      rewardedAdsWatched: data.rewardedAdsWatched + 1,
    };
    this.save(next);
    return next;
  }

  startTutorial(data: SaveData): SaveData {
    const next: SaveData = {
      ...data,
      tutorialStartedCount: data.tutorialStartedCount + 1,
    };
    this.save(next);
    return next;
  }

  completeTutorial(data: SaveData): SaveData {
    const reward = data.onboardingRewardClaimed ? 0 : 100;
    const next: SaveData = {
      ...data,
      totalGold: data.totalGold + reward,
      onboardingCompleted: true,
      onboardingSkipped: false,
      onboardingRewardClaimed: true,
      tutorialCompleted: true,
    };
    this.save(next);
    return next;
  }

  skipOnboarding(data: SaveData): SaveData {
    const next: SaveData = {
      ...data,
      onboardingCompleted: false,
      onboardingSkipped: true,
      tutorialSkipped: true,
    };
    this.save(next);
    return next;
  }

  discoverFeature(data: SaveData, featureId: string): SaveData {
    if (data.discoveredFeatures.includes(featureId)) return data;
    const next: SaveData = {
      ...data,
      discoveredFeatures: [...data.discoveredFeatures, featureId],
      discoveryCardsSeenCount: data.discoveryCardsSeenCount + 1,
    };
    this.save(next);
    return next;
  }

  markWhatsNewSeen(data: SaveData, version: string): SaveData {
    const next: SaveData = { ...data, lastSeenWhatsNewVersion: version };
    this.save(next);
    return next;
  }

  updateProfile(data: SaveData, name: string, avatar: PlayerAvatar): SaveData {
    const safeName = name.trim().replace(/\s+/g, " ").slice(0, 18);
    const next: SaveData = {
      ...data,
      profile: {
        name: safeName || "Vault Runner",
        avatar,
        createdAt: data.profile.createdAt ?? new Date().toISOString(),
        lastSeenAt: new Date().toISOString(),
      },
    };
    this.save(next);
    return next;
  }

  private migrate(parsed: Partial<SaveData>): SaveData {
    const fallback = defaultSave();
    const number = (value: unknown, defaultValue: number): number =>
      typeof value === "number" && Number.isFinite(value) && value >= 0
        ? value
        : defaultValue;
    const unlockedVaults = Array.isArray(parsed.unlockedVaults)
      ? parsed.unlockedVaults.filter((vault): vault is VaultId => validVaults.has(vault as VaultId))
      : fallback.unlockedVaults;
    if (!unlockedVaults.includes("street")) unlockedVaults.unshift("street");

    return {
      version: 1,
      highScore: number(parsed.highScore, fallback.highScore),
      totalRuns: Math.floor(number(parsed.totalRuns, fallback.totalRuns)),
      totalDiamondsCollected: Math.floor(number(
        parsed.totalDiamondsCollected,
        fallback.totalDiamondsCollected,
      )),
      totalGold: Math.floor(number(parsed.totalGold, fallback.totalGold)),
      rewardedAdsWatched: Math.floor(number(
        parsed.rewardedAdsWatched,
        fallback.rewardedAdsWatched,
      )),
      onboardingCompleted: typeof parsed.onboardingCompleted === "boolean"
        ? parsed.onboardingCompleted
        : fallback.onboardingCompleted,
      onboardingSkipped: typeof parsed.onboardingSkipped === "boolean"
        ? parsed.onboardingSkipped
        : fallback.onboardingSkipped,
      onboardingRewardClaimed: typeof parsed.onboardingRewardClaimed === "boolean"
        ? parsed.onboardingRewardClaimed
        : fallback.onboardingRewardClaimed,
      discoveredFeatures: Array.isArray(parsed.discoveredFeatures)
        ? parsed.discoveredFeatures.filter((value): value is string => typeof value === "string")
        : fallback.discoveredFeatures,
      lastSeenWhatsNewVersion: typeof parsed.lastSeenWhatsNewVersion === "string"
        ? parsed.lastSeenWhatsNewVersion
        : null,
      tutorialStartedCount: Math.floor(number(
        parsed.tutorialStartedCount,
        fallback.tutorialStartedCount,
      )),
      tutorialCompleted: typeof parsed.tutorialCompleted === "boolean"
        ? parsed.tutorialCompleted
        : fallback.tutorialCompleted,
      tutorialSkipped: typeof parsed.tutorialSkipped === "boolean"
        ? parsed.tutorialSkipped
        : fallback.tutorialSkipped,
      discoveryCardsSeenCount: Math.floor(number(
        parsed.discoveryCardsSeenCount,
        fallback.discoveryCardsSeenCount,
      )),
      playerXp: Math.floor(number(parsed.playerXp, fallback.playerXp)),
      playerRank: Math.max(1, Math.floor(number(parsed.playerRank, fallback.playerRank))),
      unlockedVaults,
      achievements: Array.isArray(parsed.achievements)
        ? parsed.achievements.filter((value): value is string => typeof value === "string")
        : fallback.achievements,
      campaignStars: parsed.campaignStars && typeof parsed.campaignStars === "object"
        ? Object.fromEntries(Object.entries(parsed.campaignStars).filter(
          ([key, value]) => Number(key) >= 1 && Number(key) <= 30
            && typeof value === "number" && value >= 0 && value <= 3,
        ))
        : fallback.campaignStars,
      highestUnlockedLevel: Math.min(30, Math.max(
        1,
        Math.floor(number(parsed.highestUnlockedLevel, fallback.highestUnlockedLevel)),
      )),
      endlessUnlocked: typeof parsed.endlessUnlocked === "boolean"
        ? parsed.endlessUnlocked
        : fallback.endlessUnlocked,
      relics: Array.isArray(parsed.relics)
        ? parsed.relics.filter((value): value is string => typeof value === "string")
        : fallback.relics,
      profile: {
        name: typeof parsed.profile?.name === "string"
          ? parsed.profile.name.trim().replace(/\s+/g, " ").slice(0, 18)
          : fallback.profile.name,
        avatar: validAvatars.has(parsed.profile?.avatar as PlayerAvatar)
          ? parsed.profile!.avatar
          : (parsed.profile as { avatar?: unknown } | undefined)?.avatar === "explorer"
            ? "cracker"
            : fallback.profile.avatar,
        createdAt: typeof parsed.profile?.createdAt === "string"
          ? parsed.profile.createdAt
          : fallback.profile.createdAt,
        lastSeenAt: typeof parsed.profile?.lastSeenAt === "string"
          ? parsed.profile.lastSeenAt
          : fallback.profile.lastSeenAt,
      },
      statistics: {
        bestCombo: Math.floor(number(parsed.statistics?.bestCombo, 0)),
        longestSurvivalSeconds: number(parsed.statistics?.longestSurvivalSeconds, 0),
        highestHeat: number(parsed.statistics?.highestHeat, 0),
        masterVaultWins: Math.floor(number(parsed.statistics?.masterVaultWins, 0)),
        rareGemsCollected: Math.floor(number(parsed.statistics?.rareGemsCollected, 0)),
        hazardsAvoided: Math.floor(number(parsed.statistics?.hazardsAvoided, 0)),
        hazardsDestroyed: Math.floor(number(parsed.statistics?.hazardsDestroyed, 0)),
        specialDiamondsCollected: Math.floor(number(
          parsed.statistics?.specialDiamondsCollected,
          0,
        )),
        endlessBestScore: Math.floor(number(parsed.statistics?.endlessBestScore, 0)),
      },
      settings: {
        reducedMotion: typeof parsed.settings?.reducedMotion === "boolean"
          ? parsed.settings.reducedMotion
          : fallback.settings.reducedMotion,
        soundEnabled: typeof parsed.settings?.soundEnabled === "boolean"
          ? parsed.settings.soundEnabled
          : fallback.settings.soundEnabled,
        musicEnabled: typeof parsed.settings?.musicEnabled === "boolean"
          ? parsed.settings.musicEnabled
          : fallback.settings.musicEnabled,
      },
    };
  }
}
